/**
 * FilterList Component
 */

import {
  getBudgetFilter,
  getExcludedSearchFilters,
  getSearchFilters,
  getSearchQuery,
  getSimilarityFilter,
  getTaxonomyQuery,
  getTempoFilter,
  hasSearchFilters
} from '../../../../contexts/queryString/selectors';
import './styles.scss';

import { trim } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { Helmet } from 'react-helmet';
import { QueryRenderer } from 'react-relay';
import { VelocityTransitionGroup } from 'velocity-react';

import { CurrencyContext } from '../../../../contexts/currency';
import { IQueryStringContext, QueryStringContext } from '../../../../contexts/queryString';
import { environment } from '../../../../relay';
import { filterListQuery } from '../../../../relay/filterList';
import { TaxonomyIdToTaxonomyType } from '../../../../relay/selectors/taxonomy';
import { RelayOperationBase } from '../../../../utils/relay';
import { Span } from '../../../atoms/text/Label';
import { BrowsePagePath } from '../../../pages/browse/Browse';
import {
  BudgetFilterTag, FilterTag, QueryFilterTag, SimilarityFilterTag, TaxonomyQueryTag, TempoFilterTag
} from './FilterTag';

interface IOwnProps extends Pick<IQueryStringContext,
  | 'removeBudgetFilter'
  | 'removeTempoFilter'
  | 'removeSearchFilter'
  | 'removeSimilarityFilter'
  | 'removeTaxonomyQuery'
  | 'clearSearchQuery'
> {
  budgetFilter: GQL.ITrackSearchBudgetFilter | undefined;
  excludedSearchFilters: SearchFilter[];
  hasFilters: boolean;
  searchFilters: SearchFilter[];
  searchQuery: SearchQuery;
  similarityFilter?: GQL.ISimilarityFilter;
  similaritySeed?: GQL.ITrackSearchResult;
  taxonomyQuery?: GQL.ITaxonomyQuery;
  tempoFilter: GQL.IIntegerRangeFilter | null | undefined;
}

interface IPropsFromQuery {
  viewer?: GQL.IViewer;
}

type FilterListProps = IOwnProps & IPropsFromQuery;

const searchQueryTagID = 'search-query';
const taxonomyQueryTagID = 'taxonomy-query';
const budgetFilterTagID = 'budget';
const tempoFilterTagID = 'tempo';
const similarityFilterTagID = 'similarity';
const classes = new BEM('filter-list');

class FilterList extends React.Component<FilterListProps> {
  onRemoveFilter = (type?: string) => (filter: SearchFilter, exclude: boolean) => {
    switch (type) {
      case searchQueryTagID:
        this.props.clearSearchQuery();
        break;
      case similarityFilterTagID:
        this.props.removeSimilarityFilter();
        break;
      case budgetFilterTagID:
        this.props.removeBudgetFilter();
        break;
      case tempoFilterTagID:
        this.props.removeTempoFilter();
        break;
      case taxonomyQueryTagID:
        this.props.removeTaxonomyQuery(filter);
        break;
      default:
        this.props.removeSearchFilter(filter, exclude);
        break;
    }
  }

  renderBudgetFilter(budgetFilter: GQL.ITrackSearchBudgetFilter) {
    return (
      <CurrencyContext.Consumer>
        {({ currency }) =>
          <BudgetFilterTag
            budget={budgetFilter}
            currency={currency}
            onRemove={this.onRemoveFilter(budgetFilterTagID)}
            {...classes('filter')}
          />
        }
      </CurrencyContext.Consumer>
    );
  }

  renderQueryFilter() {
    return (
      <QueryFilterTag
        query={this.props.searchQuery}
        onRemove={this.onRemoveFilter(searchQueryTagID)}
        {...classes('filter')}
      />
    );
  }

  renderSimilarityFilter() {
    if (!this.props.similaritySeed) {
      return;
    }
    const track = this.props.similaritySeed;
    const trackTitle = `${track.title} by ${track.artist!.name}`;
    return (
      <SimilarityFilterTag
        trackTitle={trackTitle}
        onRemove={this.onRemoveFilter(similarityFilterTagID)}
        {...classes('filter')}
      />
    );
  }

  renderTaxonomyQuery() {
    const taxonomyQuery = this.props.taxonomyQuery!;
    const filter = {
      name: taxonomyQuery.query,
      taxonomy: {
        id: taxonomyQuery.taxonomy_id,
        name: TaxonomyIdToTaxonomyType[taxonomyQuery.taxonomy_id],
      },
    };
    return (
      <TaxonomyQueryTag
        filter={filter}
        taxonomyQuery={this.props.taxonomyQuery!}
        onRemove={this.onRemoveFilter(taxonomyQueryTagID)}
        {...classes('filter')}
      />
    );
  }

  renderTempoFilter() {
    return (
      <TempoFilterTag
        tempo={this.props.tempoFilter!}
        onRemove={this.onRemoveFilter(tempoFilterTagID)}
        {...classes('filter')}
      />
    );
  }

  renderFilter(filter: SearchFilter, exclude: boolean) {
    return (
      <FilterTag
        key={filter.name}
        exclude={!!exclude}
        label={filter.name}
        filter={filter}
        onRemove={this.onRemoveFilter()}
        {...classes('filter')}
      />
    );
  }

  render() {
    const {
      budgetFilter,
      excludedSearchFilters,
      searchFilters,
      hasFilters,
      searchQuery,
      similarityFilter,
      taxonomyQuery,
      tempoFilter,
    } = this.props;

    return (
      <div {...classes() }>
        {hasFilters && document.location.pathname === BrowsePagePath && (
          <Helmet>
            <title>
              {similarityFilter ?
                'Similar Tracks' :
                `Results for ${trim([...searchFilters.map((f) => f.name), searchQuery].join(', '), ', ')}`}
            </title>
          </Helmet>
        )}
        <div {...classes('wrapper') }>
          <VelocityTransitionGroup
            component="div"
            enter={{ animation: 'transition.slideRightIn', duration: 250, delay: 500 }}
            leave={{ animation: 'transition.fadeOut', duration: 250 }}
            {...classes('scroller') }
          >
            {searchFilters.map((filter) => this.renderFilter(filter, false))}
            {searchQuery && this.renderQueryFilter()}
            {similarityFilter && this.renderSimilarityFilter()}
            {taxonomyQuery && this.renderTaxonomyQuery()}
            {tempoFilter && this.renderTempoFilter()}
            {budgetFilter && this.renderBudgetFilter(budgetFilter)}
            {excludedSearchFilters.map((filter) => this.renderFilter(filter, true))}
          </VelocityTransitionGroup>
        </div>
      </div>
    );
  }
}

const FilterListWithQueryStringContext: React.SFC = (props) => (
  <QueryStringContext.Consumer>
    {(ctx) => {
      const similaritySeedId = ctx.filters && ctx.filters.similarity && ctx.filters.similarity.track_id;
      return (
        <QueryRenderer<RelayOperationBase<{}, IPropsFromQuery>>
          environment={environment}
          query={filterListQuery}
          variables={{
            // Can't pass undefined/null value as the node.id parameter is non-nullable
            // and is statically validated
            similarity_seed_id: similaritySeedId || '',
            similarity_search: !!similaritySeedId,
          }}
          render={({ error, props: propsFromQuery }) => {
            if (error) {
              return <Span size="small" textColor="dangerous">{error.message}</Span>;
            }

            // tslint:disable-next-line:max-line-length
            const similaritySeed = propsFromQuery && propsFromQuery.viewer && propsFromQuery.viewer.node ?
              propsFromQuery.viewer.node as GQL.ITrackSearchResult :
              undefined;

            return (
              <FilterList
                budgetFilter={getBudgetFilter(ctx)}
                tempoFilter={getTempoFilter(ctx)}
                excludedSearchFilters={getExcludedSearchFilters(ctx)}
                hasFilters={hasSearchFilters(ctx)}
                searchQuery={getSearchQuery(ctx)}
                searchFilters={getSearchFilters(ctx)}
                similarityFilter={getSimilarityFilter(ctx)}
                similaritySeed={similaritySeed}
                taxonomyQuery={getTaxonomyQuery(ctx)}
                removeBudgetFilter={ctx.removeBudgetFilter}
                removeSearchFilter={ctx.removeSearchFilter}
                removeSimilarityFilter={ctx.removeSimilarityFilter}
                removeTempoFilter={ctx.removeTempoFilter}
                removeTaxonomyQuery={ctx.removeTaxonomyQuery}
                clearSearchQuery={ctx.clearSearchQuery}
                {...props}
              />
            );
          }}
        />
      );
    }}
  </QueryStringContext.Consumer>
);

export default FilterListWithQueryStringContext;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/organisms/search/FilterList/index.tsx